import {
    Box,
    Button,
    HStack,
    IconButton,
    Input,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
} from '@chakra-ui/react';
import { formatPartNumber } from '../../functions/formatPartNumber';
import { Icon, InsetTable } from '../../ui';
import {
    PartNumberEditorRow,
    nextPartNumberRowKey,
} from '../../utils/inventoryPartNumbers';

interface InventoryPartNumbersFieldProps {
    value: Array<PartNumberEditorRow>;
    onChange: (rows: Array<PartNumberEditorRow>) => void;
    isDisabled?: boolean;
    /** Optional error under the table (e.g. duplicates / empty). */
    error?: string;
}

const InventoryPartNumbersField = ({
    value,
    onChange,
    isDisabled = false,
    error,
}: InventoryPartNumbersFieldProps) => {
    const updateRow = (key: string, patch: Partial<PartNumberEditorRow>) => {
        onChange(value.map((row) => (row.key === key ? { ...row, ...patch } : row)));
    };

    const setPrimary = (key: string) => {
        onChange(value.map((row) => ({ ...row, isPrimary: row.key === key })));
    };

    const blurNormalize = (key: string) => {
        const row = value.find((r) => r.key === key);
        if (!row) return;
        const stripped = formatPartNumber(row.value);
        if (stripped !== row.value) {
            updateRow(key, { value: stripped });
        }
    };

    const addRow = () => {
        onChange([
            ...value,
            { key: nextPartNumberRowKey(), value: '', isPrimary: false },
        ]);
    };

    const removeRow = (key: string) => {
        if (value.length <= 1) return;
        const removing = value.find((r) => r.key === key);
        const next = value.filter((r) => r.key !== key);
        if (removing?.isPrimary && next.length > 0) {
            next[0] = { ...next[0], isPrimary: true };
        }
        onChange(next);
    };

    return (
        <InsetTable
            footer={
                <HStack
                    px={3}
                    py={2}
                    justify="space-between"
                    align="center"
                    borderTopWidth="1px"
                    borderColor="border.subtle"
                >
                    <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        leftIcon={<Icon name="plus" size="sm" />}
                        onClick={addRow}
                        isDisabled={isDisabled}
                        color="fg.muted"
                        fontWeight="medium"
                        px={1}
                        _hover={{ bg: 'transparent', color: 'fg.default' }}
                    >
                        Add part number
                    </Button>
                    {error ? (
                        <Text fontSize="xs" color="error.fg">
                            {error}
                        </Text>
                    ) : null}
                </HStack>
            }
        >
                <Thead>
                    <Tr>
                        <Th w="1%" whiteSpace="nowrap" textAlign="center">
                            Default
                        </Th>
                        <Th>Part Number</Th>
                        <Th w="44px" />
                    </Tr>
                </Thead>
                <Tbody>
                    {value.map((row) => (
                        <Tr key={row.key}>
                            <Td textAlign="center" verticalAlign="middle">
                                <Box
                                    as="button"
                                    type="button"
                                    role="radio"
                                    aria-checked={row.isPrimary}
                                    aria-label={`Set ${row.value || 'row'} as default`}
                                    disabled={isDisabled}
                                    w="16px"
                                    h="16px"
                                    borderRadius="full"
                                    borderWidth="2px"
                                    borderColor={row.isPrimary ? 'brand.solid' : 'border.strong'}
                                    bg={row.isPrimary ? 'brand.solid' : 'bg.surface'}
                                    display="inline-flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    verticalAlign="middle"
                                    cursor={isDisabled ? 'not-allowed' : 'pointer'}
                                    opacity={isDisabled ? 0.5 : 1}
                                    transition="border-color 0.15s ease, background 0.15s ease"
                                    _hover={isDisabled ? undefined : { borderColor: 'brand.solid' }}
                                    onClick={() => {
                                        if (!isDisabled) setPrimary(row.key);
                                    }}
                                >
                                    {row.isPrimary ? (
                                        <Box w="6px" h="6px" borderRadius="full" bg="bg.surface" />
                                    ) : null}
                                </Box>
                            </Td>
                            <Td verticalAlign="middle">
                                <Input
                                    variant="unstyled"
                                    size="sm"
                                    fontSize="sm"
                                    fontFamily="mono"
                                    color="fg.default"
                                    value={row.value}
                                    onChange={(e) =>
                                        updateRow(row.key, { value: e.target.value })
                                    }
                                    onBlur={() => blurNormalize(row.key)}
                                    isDisabled={isDisabled}
                                    placeholder="Enter part number"
                                    _placeholder={{ color: 'fg.subtle' }}
                                    autoFocus={!row.value}
                                />
                            </Td>
                            <Td verticalAlign="middle" textAlign="right">
                                <IconButton
                                    aria-label="Remove part number"
                                    icon={<Icon name="trash" size="sm" />}
                                    size="xs"
                                    variant="ghost"
                                    color="fg.subtle"
                                    _hover={{ color: 'fg.muted', bg: 'bg.muted' }}
                                    isDisabled={isDisabled || value.length <= 1}
                                    onClick={() => removeRow(row.key)}
                                />
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
        </InsetTable>
    );
};

export default InventoryPartNumbersField;
